import { Tabs, TitleHeader } from '@innovamat/glimmer-components';
import type { Classroom, Course } from '@innovamat/glow-api-client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { StageName } from '../../shared';
import { useStage } from '../../shared';
import { GeneralProgress } from './general-progress';
import { WeeklyResults } from './weekly-results';

export type TableType = 'WeeklyResults' | 'GeneralProgress';

type TabItem = {
  tableType: TableType;
  labelKey: string;
};

type Props = {
  setEventData: (eventType: string, eventProperties?: any) => void;
  classroom: Classroom;
  courses: Course[];
  standard: string;
};

export function Reports({
  setEventData,
  classroom,
  courses,
  standard,
}: Props): JSX.Element {
  //TODO: legacy? remove this file?
  const [tableActive, setTableActive] = useState<TableType>('WeeklyResults');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { courseOrder, id, name } = classroom;
  const {
    stage: educationalStage,
    isLearningPathExperience,
    isClassroomManagerExperience,
  } = useStage({
    courseOrder: courseOrder || 0,
  });
  const isTableActive = (table: TableType): boolean => tableActive === table;

  useEffect(() => {
    const appContent = document.getElementById('app-content');
    if (appContent) {
      appContent.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
    }
    return () => {
      if (appContent) {
        appContent.style.overflow = '';
        document.body.style.overflow = 'scroll';
      }
    };
  }, []);

  const changeTableActive = (table: TableType): void => {
    setTableActive(table);
    navigate({ search: '' });
    setEventData('report_change_tab', {
      classroom_id: id,
      dashbord_tab: table,
    });
  };

  const tabs: TabItem[] = [
    { tableType: 'WeeklyResults', labelKey: 'reports.weeklyResults' },
    { tableType: 'GeneralProgress', labelKey: 'reports.generalProgress' },
  ];

  const createTabObject = (tab: TabItem) => ({
    selected: isTableActive(tab.tableType),
    text: t(tab.labelKey),
    onClick: () => changeTableActive(tab.tableType),
    dataTestId: `${tab.tableType.toUpperCase()}_TAB`,
  });

  const handleNavigate = (route: string, queryParams?: string): void => {
    navigate(
      route,
      queryParams ? { state: { search: queryParams } } : undefined
    );
  };

  const handleNavigateToResource = (appletId: string) => {
    window.open(`/curriculum/applet/${appletId}`, '_blank');
  };
  const locationInfo = {
    search: location.search,
    pathname: location.pathname,
  };

  return (
    <Wrapper>
      <Container>
        <TitleHeader
          title={t('reports.title')}
          hasSeparator={false}
          style={{ position: 'sticky', left: 0 }}
        />
        <TabTables id="tab-tables">
          <Tabs tabs={tabs.map(createTabObject)} />
        </TabTables>
        <TableContainer id="reports-table-container">
          {tableActive === 'WeeklyResults' ? (
            <WeeklyResults
              setEventData={setEventData}
              isClassroomManagerExperience={isClassroomManagerExperience}
              isLearningPathExperience={isLearningPathExperience}
              educationalStage={educationalStage as StageName}
              courses={courses}
              handleNavigateToResource={handleNavigateToResource}
              handleNavigate={handleNavigate}
              locationInfo={locationInfo}
              classroom={classroom}
            />
          ) : (
            <GeneralProgress
              setEventData={setEventData}
              classroomId={id}
              isClassroomManagerExperience={isClassroomManagerExperience}
              classroomName={name || ''}
              educationalStage={educationalStage as StageName}
              courses={courses}
              standard={standard}
              handleNavigate={handleNavigate}
              handleNavigateToResource={handleNavigateToResource}
            />
          )}
          <div id="portal-root" />
        </TableContainer>
      </Container>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-right: -24px;
  margin-bottom: 32px;
`;

const Container = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    max-width: 1440px;
    margin: 0 auto;
    display: block;
  }
`;

const TableContainer = styled.div`
  position: relative;
`;

const TabTables = styled.nav`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  border-bottom: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-subtle'].value};
`;
